import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { DeskCatalogsPutBody } from '@erp/shared';
import { api } from '@/lib/api-client';
import { Checkbox } from '@/components/ui/checkbox';
import { EmptyState, Skeleton } from '@/components/ui/misc';
import { cn } from '@/lib/utils';
import { deskCatalogIdsOptions, errorMessage } from '@/features/desks/desks-queries';
import { catalogsListOptions } from '@/features/catalogs/catalogs-queries';

export function DeskCatalogsSection({ deskId }: { deskId: number }) {
  const queryClient = useQueryClient();
  const [catalogs, linked] = useQueries({
    queries: [catalogsListOptions(), deskCatalogIdsOptions(deskId)],
  });

  const save = useMutation({
    mutationFn: async (body: DeskCatalogsPutBody) => {
      const res = await api<unknown>(`/desks/${deskId}/catalogs`, { method: 'PUT', body });
      if (res.error) throw res.error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: deskCatalogIdsOptions(deskId).queryKey });
    },
  });

  const linkedIds = new Set(linked.data ?? []);

  const toggle = (catalogId: number, checked: boolean) => {
    const next = new Set(linkedIds);
    if (checked) next.add(catalogId);
    else next.delete(catalogId);
    save.mutate({ catalogIds: [...next] });
  };

  const pending = catalogs.isPending || linked.isPending;
  const error = catalogs.error ?? linked.error ?? save.error;

  return (
    <section className="rounded-lg border bg-card p-4 shadow-xs">
      <div className="mb-1 flex items-center gap-2">
        <h2 className="text-base font-semibold">Catálogos</h2>
        {linked.data ? (
          <span className="rounded-full bg-accent px-2 py-0.5 text-xs font-medium text-muted-foreground tabular-nums">
            {linked.data.length}
          </span>
        ) : null}
      </div>
      <p className="mb-3 text-[13px] text-muted-foreground">
        Catálogos de serviço disponíveis para os tickets desta mesa.
      </p>
      {pending ? (
        <div className="space-y-2" aria-busy>
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-9 w-full" />
          ))}
        </div>
      ) : catalogs.isError || linked.isError ? (
        <EmptyState className="h-24" message={`Erro ao carregar catálogos: ${errorMessage(error)}`} />
      ) : (catalogs.data ?? []).length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Nenhum catálogo cadastrado.{' '}
          <Link to="/settings/desks/catalogs" className="font-medium text-primary-pressed hover:underline">
            Criar catálogo
          </Link>
        </p>
      ) : (
        <ul className="space-y-1.5">
          {(catalogs.data ?? []).map((c) => (
            <li key={c.id}>
              <label
                className={cn(
                  'flex cursor-pointer items-center gap-2.5 rounded-md border border-divider px-3 py-2 text-sm transition-colors hover:border-border',
                  save.isPending && 'pointer-events-none opacity-60'
                )}
              >
                <Checkbox
                  checked={linkedIds.has(c.id)}
                  disabled={save.isPending}
                  onCheckedChange={(checked) => toggle(c.id, checked === true)}
                />
                <span className="min-w-0 truncate font-medium">{c.name}</span>
                {!c.active ? (
                  <span className="ml-auto shrink-0 text-xs text-muted-foreground">Inativo</span>
                ) : null}
              </label>
            </li>
          ))}
        </ul>
      )}
      {save.error ? <p className="mt-2 text-sm text-destructive">{errorMessage(save.error)}</p> : null}
    </section>
  );
}
